import patient360Data from '../../mocks/patient360Data.json';
import { GAP_TEMPLATES } from './templates';
import {
  type GapDomain,
  type GapQuestion,
  type GapQuestionSourceTimestamps,
  type GapSeverity,
  type GapStatus,
} from './types';

const daysSince = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const diff = Date.now() - parsed.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const suggestGaps = (patientId: string): GapQuestion[] => {
  const details = (patient360Data.patientDetails as Record<string, {
    clinicalInfo?: { lastUpdated?: string; data?: { primaryDiagnosis?: string } };
    cgmData?: { lastUpdated?: string; data?: { timeInRange?: { target?: number } } };
    nutritionData?: { lastUpdated?: string; data?: { mealLoggingCompliance?: string; lastMealLogged?: string } };
    carePlan?: {
      lastUpdated?: string;
      data?: { goals?: Array<{ goal: string; targetDate: string; status: string; progress: number }> };
    };
    socialDeterminants?: { lastUpdated?: string; data?: { foodSecurity?: { status?: string } } };
  }>)[patientId];

  const cgmDays = daysSince(details?.cgmData?.lastUpdated);
  const nutritionDays = daysSince(details?.nutritionData?.lastUpdated);
  const lastMealLoggedDays = daysSince(details?.nutritionData?.data?.lastMealLogged);
  const socialDays = daysSince(details?.socialDeterminants?.lastUpdated);
  const carePlanDays = daysSince(details?.carePlan?.lastUpdated);
  const mealLoggingCompliance = Number.parseInt(details?.nutritionData?.data?.mealLoggingCompliance ?? '', 10);
  const goals = details?.carePlan?.data?.goals ?? [];
  const overdueGoals = goals.filter((goal) => goal.status === 'IN_PROGRESS' && daysSince(goal.targetDate) !== null && daysSince(goal.targetDate)! > 0);
  const lowProgressGoals = goals.filter((goal) => goal.status === 'IN_PROGRESS' && goal.progress < 50);
  const latestGoalTargetDate = goals
    .map((goal) => goal.targetDate)
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];

  const sourceTimestamps: GapQuestionSourceTimestamps = {
    analysisGeneratedAt: new Date().toISOString(),
    clinicalDataUpdatedAt: details?.clinicalInfo?.lastUpdated,
    cgmDataUpdatedAt: details?.cgmData?.lastUpdated,
    nutritionDataUpdatedAt: details?.nutritionData?.lastUpdated,
    lastMealLoggedAt: details?.nutritionData?.data?.lastMealLogged,
    carePlanUpdatedAt: details?.carePlan?.lastUpdated,
    socialDataUpdatedAt: details?.socialDeterminants?.lastUpdated,
    latestGoalTargetDate,
  };

  return GAP_TEMPLATES.map((template) => {
    let severity: GapSeverity = template.defaultSeverity;
    let gapStatus: GapStatus = 'MISSING';
    const recencyNotes: string[] = [];

    if (template.domain === 'CLINICAL') {
      const cgmTarget = details?.cgmData?.data?.timeInRange?.target;
      if (typeof cgmTarget === 'number' && cgmTarget < 70) severity = 'HIGH';
    }

    if (template.domain === 'SDOH') {
      const foodSecurity = details?.socialDeterminants?.data?.foodSecurity?.status;
      if (foodSecurity === 'AT_RISK') severity = 'HIGH';
    }

    if (template.gapKey === 'hba1c_trend' && cgmDays !== null && cgmDays > 14) {
      severity = 'HIGH';
      gapStatus = 'STALE';
      recencyNotes.push(`CGM data is ${cgmDays} days old.`);
    }

    if (template.gapKey === 'meal_timing_patterns') {
      if ((lastMealLoggedDays !== null && lastMealLoggedDays > 3) || (!Number.isNaN(mealLoggingCompliance) && mealLoggingCompliance < 80)) {
        severity = 'HIGH';
        gapStatus = 'STALE';
      }
      if (lastMealLoggedDays !== null) {
        recencyNotes.push(`Last meal log was ${lastMealLoggedDays} days ago.`);
      }
      if (!Number.isNaN(mealLoggingCompliance)) {
        recencyNotes.push(`Meal logging compliance is ${mealLoggingCompliance}%.`);
      }
    }

    if (template.gapKey === 'goal_progress_review') {
      if ((carePlanDays !== null && carePlanDays > 30) || overdueGoals.length > 0 || lowProgressGoals.length > 0) {
        severity = 'HIGH';
        gapStatus = overdueGoals.length > 0 || lowProgressGoals.length > 0 ? 'LOW_CONF' : 'STALE';
      }
      if (carePlanDays !== null) {
        recencyNotes.push(`Care plan was last updated ${carePlanDays} days ago.`);
      }
      if (overdueGoals.length > 0) {
        recencyNotes.push(`${overdueGoals.length} active goal(s) are past target date.`);
      }
      if (lowProgressGoals.length > 0) {
        recencyNotes.push(`${lowProgressGoals.length} active goal(s) remain below 50% progress.`);
      }
    }

    if ((template.domain === 'CULTURAL' || template.domain === 'SDOH') && socialDays !== null && socialDays > 45) {
      severity = severity === 'LOW' ? 'MEDIUM' : severity;
      gapStatus = 'STALE';
      recencyNotes.push(`Social determinants data is ${socialDays} days old.`);
    }

    if (template.domain === 'BEHAVIORAL' && template.gapKey !== 'goal_progress_review' && nutritionDays !== null && nutritionDays > 14) {
      severity = severity === 'LOW' ? 'MEDIUM' : severity;
      gapStatus = 'STALE';
      recencyNotes.push(`Nutrition data is ${nutritionDays} days old.`);
    }

    return template.questions.map((questionTemplate, index): GapQuestion => ({
      id: `${template.gapKey}-q${index}-${patientId}`,
      domain: template.domain as GapDomain,
      gapKey: template.gapKey,
      gapLabel: template.gapLabel,
      gapStatus,
      severity,
      question: questionTemplate.question,
      rationale: recencyNotes.length > 0
        ? `${questionTemplate.rationale} ${recencyNotes.join(' ')}`
        : questionTemplate.rationale,
      included: severity === 'HIGH',
      sourceTimestamps,
      resolutionStatus: 'OPEN',
    }));
  }).flat();
};